'use client';
import React, { createContext, useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { LoginScreen } from '@/components/auth/LoginScreen';
import {
  decodeToken,
  isExpired,
  logout as clearSession,
  refreshSession,
  tokenStore,
  type UserProfile,
} from '@/lib/auth';

interface AuthContextValue {
  ready: boolean;
  authenticated: boolean;
  token?: string;
  userProfile?: UserProfile;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextValue>({
  ready: false,
  authenticated: false,
  logout: () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  // Restaura a sessão de um F5: se o token guardado expirou, tenta renovar antes
  // de mandar o usuário para o login.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      let stored = tokenStore.access();
      if (stored && isExpired(stored)) stored = await refreshSession();
      if (!cancelled) {
        setToken(stored);
        setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // O axios avisa quando o refresh falhou no meio de uma requisição.
  useEffect(() => {
    const onSessionEnded = () => setToken(null);
    window.addEventListener('castanha:session-ended', onSessionEnded);
    return () => window.removeEventListener('castanha:session-ended', onSessionEnded);
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setToken(null);
  }, []);

  const handleAuthenticated = useCallback(() => setToken(tokenStore.access()), []);

  if (!ready) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!token) return <LoginScreen onAuthenticated={handleAuthenticated} />;

  const userProfile = decodeToken(token) ?? undefined;

  return (
    <AuthContext.Provider value={{ ready, authenticated: true, token, userProfile, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
